// Shared clock: the current time, updated while a screen uses it, so that
// remaining times count down on their own (a page left open never shows a
// stale "10 min"). One timer per interval, whatever the number of users.
import { onBeforeUnmount, onMounted, ref } from "vue";

const clocks = new Map(); // interval (ms) -> { now, timer, users }

const clockFor = (interval) => {
  if (!clocks.has(interval))
    clocks.set(interval, { now: ref(Date.now()), timer: null, users: 0 });
  return clocks.get(interval);
};

// interval: 1000 for a countdown to the second, more for coarser displays
export const useNow = (interval = 1000) => {
  const clock = clockFor(interval);
  onMounted(() => {
    clock.users += 1;
    if (clock.timer === null) {
      clock.now.value = Date.now();
      clock.timer = setInterval(() => (clock.now.value = Date.now()), interval);
    }
  });
  onBeforeUnmount(() => {
    clock.users -= 1;
    if (clock.users <= 0 && clock.timer !== null) {
      clearInterval(clock.timer);
      clock.timer = null;
      clock.users = 0;
    }
  });
  return clock.now;
};

// Date of the API ("2026-10-14 21:30:00", local time of the user) → ms.
// The "T" is needed by Safari (iPhone), which cannot read the space form.
export const parseTime = (value) => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "number") return value;
  if (!value) return NaN;
  return new Date(String(value).replace(" ", "T")).getTime();
};

// API date → Date, null when unreadable (an "Invalid Date" makes the
// formatting throw and the whole screen fail)
export const toDate = (value) => {
  const time = parseTime(value);
  return Number.isNaN(time) ? null : new Date(time);
};

// Date in French ("21 oct. 2026"), fallback when missing or unreadable
export const formatDate = (
  value,
  options = { dateStyle: "medium" },
  fallback = "",
) => {
  const date = toDate(value);
  return date ? new Intl.DateTimeFormat("fr-FR", options).format(date) : fallback;
};

const pad = (value) => String(value).padStart(2, "0");

// Remaining time until a date: "42 s", "9 min 05 s" under an hour (to the
// second), "3 h 25", "2 j 4 h"; "Terminée" once passed, "" when unknown
export const formatRemaining = (target, now = Date.now()) => {
  const end = parseTime(target);
  if (Number.isNaN(end)) return "";
  const seconds = Math.floor((end - now) / 1000);
  if (seconds <= 0) return "Terminée";
  if (seconds < 60) return `${seconds} s`;
  if (seconds < 3600)
    return `${Math.floor(seconds / 60)} min ${pad(seconds % 60)} s`;
  if (seconds < 86400)
    return `${Math.floor(seconds / 3600)} h ${pad(Math.floor((seconds % 3600) / 60))}`;
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  return hours ? `${days} j ${hours} h` : `${days} j`;
};

// Hours left before a date (negative once passed), NaN when unknown
export const hoursLeft = (target, now = Date.now()) =>
  (parseTime(target) - now) / 3600000;
